// ── Tax lots adapter ────────────────────────────────────────────────
// Reduces transactions to the currently-open FIFO lots per coin.
// Returns: [{ amount, buyDate, costPer }] sorted oldest → newest.
//
// Used by TaxTimeline and SmartCheckMini. Derives the open lots DIRECTLY
// from the canonical FIFO engine (buildFifoPool / fifo.js) — the documented
// Single Source of Truth that also feeds the displayed portfolio balance
// (Dashboard → calcPortfolio → buildFifoPool). This guarantees by
// construction that Σ(offene Lots) == aktueller Bestand (Invariant 5).
//
// History: this file previously re-implemented its OWN partial FIFO. That
// copy had no `einzahlung` branch (incoming transfer legs were never re-added),
// silently swallowed over-consumption, depended on the non-guaranteed
// `linkedTxId` field, and ignored bridge/burn/loss/futures/coin-fee types.
// Result: the open-lot sum diverged massively from the real balance
// (USDC zeigte 5,5735 „gebunden" bei echtem Bestand 0,8256). Jeder Coin mit
// Transfer-/Einzahlungs-Legs war betroffen. Deshalb: keine zweite Pool-
// Wahrheit mehr — immer durch fifo.js. Siehe lotSystem.js (gleiches Prinzip).

import { buildFifoPool } from './fifo';
import { isLotFree, lotFreeMs, daysUntilFree } from './holdingPeriod';

// buildFifoPool verarbeitet die GESAMTE TX-Historie. SmartCheckMini ruft
// buildTaxLots einmal PRO Coin auf (availableCoins) — ohne Cache wäre das
// O(coins × allTxs) FIFO-Läufe pro Render. Cache je txs-Array-Identität:
// innerhalb eines Renders ist es dieselbe Referenz, also ein FIFO-Lauf total.
const _poolCache = new WeakMap();

function poolFor(txs) {
  if (!Array.isArray(txs)) return {};
  let pool = _poolCache.get(txs);
  if (!pool) {
    pool = buildFifoPool(txs).pool || {};
    _poolCache.set(txs, pool);
  }
  return pool;
}

export function buildTaxLots(sym, txs) {
  if (!sym || !txs) return [];
  const target = String(sym).toUpperCase();
  const lots = poolFor(txs)[target] || [];

  return lots
    // Nur echte offene Mengen. Schließt verbrauchte Lots (rest≈0) UND
    // overConsumption-Schuld-Lots (rest negativ) aus.
    .filter(l => l.rest > 1e-9)
    .map(l => ({
      amount: l.rest,
      buyDate: l.datum,
      costPer: Number(l.stueck) || 0,
    }))
    .sort((a, b) => +new Date(a.buyDate) - +new Date(b.buyDate));
}

/**
 * Aggregate stats across the lots: total amount, tax-free amount, etc.
 * `today` defaults to now.
 */
export function summarizeLots(lots, today = new Date()) {
  // Frist-Logik zentral in holdingPeriod.js (Jahrestag + 1 Tag, BGB §188 II) —
  // identisch zur Steuer-Engine (fifo.js) und zu den Alerts. Nicht „rollend 365".
  let total = 0, free = 0, taxable = 0;
  let nextFreeLot = null;
  for (const lot of lots) {
    total += lot.amount;
    if (isLotFree(lot.buyDate, today)) {
      free += lot.amount;
    } else {
      taxable += lot.amount;
      if (!nextFreeLot || +new Date(lot.buyDate) < +new Date(nextFreeLot.buyDate)) {
        nextFreeLot = lot;
      }
    }
  }
  const freeMs = nextFreeLot ? lotFreeMs(nextFreeLot.buyDate) : null;
  const nextFreeDate = freeMs != null ? new Date(freeMs) : null;
  const daysToNextFree = nextFreeLot ? daysUntilFree(nextFreeLot.buyDate, today) : null;
  return { total, free, taxable, nextFreeLot, nextFreeDate, daysToNextFree };
}
